import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    User, Users, Building2, ArrowRight, Eye, EyeOff, Phone, Check,
    ShieldCheck, BarChart3, TrendingUp, Headphones, Lock,
} from "lucide-react";
import { LOGO_DARK_BG } from "@/lib/brand";
import { CONTACT } from "@/lib/brand";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_ebg-realty-pro/artifacts/bqs0ziee_home%20page%20hero.png";

const ROLES = [
    { k: "buyer",   l: "Buyer",           icon: User,      desc: "Find & Invest in properties" },
    { k: "partner", l: "Channel Partner", icon: Users,     desc: "Manage leads & earn more" },
    { k: "builder", l: "Builder",         icon: Building2, desc: "Manage projects & inventory" },
];

const TRUST = [
    { Icon: ShieldCheck, l: "Trusted & Verified",   sub: "100% Verified Properties" },
    { Icon: TrendingUp,  l: "End-to-End Solutions", sub: "From Search to Possession" },
    { Icon: BarChart3,   l: "Intelligent Insights", sub: "Data-driven Market Intelligence" },
    { Icon: Headphones,  l: "Dedicated Support",    sub: "Personalised Help at Every Step" },
];

export default function Login() {
    const nav = useNavigate();
    const loc = useLocation();
    const { login } = useAuth();
    const initialRole = new URLSearchParams(loc.search).get("role") || "buyer";
    const [role, setRole]         = useState(initialRole);
    const [email, setEmail]       = useState("");
    const [pw, setPw]             = useState("");
    const [showPw, setShowPw]     = useState(false);
    const [remember, setRemember] = useState(true);
    const [busy, setBusy]         = useState(false);

    const dest = (r) => r === "admin" ? "/admin/dashboard" : `/portal/${r}`;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = await login(email.trim(), pw);
        setBusy(false);
        if (!res.ok) { toast.error(res.error); return; }
        if (res.user.role === "admin") {
            toast.error("Admins must sign in from /admin.");
            return;
        }
        toast.success(`Welcome back, ${res.user.name}`);
        nav(dest(res.user.role));
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ebg-ivory)]">
            {/* ————— Left: Brand Hero ————— */}
            <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#0a0d18] text-white p-8 xl:p-10">
                <img src={HERO_IMG} alt="Luxury home" className="absolute inset-0 w-full h-full object-cover opacity-45"/>
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0d18]/95 via-[#0a0d18]/70 to-[#0a0d18]/30"/>

                {/* Logo */}
                <div className="relative">
                    <Link to="/">
                        <img src={LOGO_DARK_BG} alt="EBG Realty" className="h-14 xl:h-16 w-auto"/>
                    </Link>
                </div>

                {/* Middle content */}
                <div className="relative max-w-xl">
                    <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)] mb-4">Welcome to EBG Realty</div>
                    <h1 className="font-serif text-4xl xl:text-[46px] leading-[1.02]">
                        India&rsquo;s Most Trusted<br/>
                        <em className="not-italic text-[var(--ebg-gold)]">Real Estate Platform.</em>
                    </h1>
                    <div className="w-16 h-px bg-[var(--ebg-gold)] my-5"/>
                    <p className="text-white/70 text-[14px] leading-relaxed max-w-md">
                        One login. Four powerful portals. Everything you need to buy, invest, sell or grow with EBG Realty — end to end.
                    </p>
                </div>

                {/* Trust strip */}
                <div className="relative">
                    <div className="border border-white/12 bg-black/40 backdrop-blur-md p-5 grid grid-cols-4 gap-3">
                        {TRUST.map((t, i) => (
                            <div key={i} className="text-center">
                                <div className="w-10 h-10 rounded-full border border-[var(--ebg-gold)]/50 flex items-center justify-center mx-auto text-[var(--ebg-gold)]">
                                    <t.Icon size={15}/>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.14em] font-bold mt-2.5 leading-tight">{t.l}</div>
                                <div className="text-[9px] text-white/55 mt-0.5 leading-snug">{t.sub}</div>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-white/50 mt-3">
                        <Lock size={12} className="text-[var(--ebg-gold)]"/>
                        Your privacy and security are our top priorities. Secure login powered by enterprise-grade encryption.
                    </div>
                </div>
            </aside>

            {/* ————— Right: Sign-in ————— */}
            <section className="relative flex flex-col p-6 md:p-8 xl:p-10 min-h-screen">
                {/* Top-right help */}
                <div className="flex items-center justify-end text-[12px]">
                    <span className="text-black/55 mr-2">Need help?</span>
                    <a href={CONTACT.tel} className="inline-flex items-center gap-1.5 text-black font-bold hover:text-[var(--ebg-gold)]">
                        <Phone size={12} className="text-[var(--ebg-gold)]"/> {CONTACT.display}
                    </a>
                </div>

                <div className="flex-1 flex items-center justify-center py-3" data-testid="login-panel">
                    <div className="w-full max-w-xl">
                        {/* Mobile logo */}
                        <Link to="/" className="lg:hidden inline-block mb-6">
                            <img src={LOGO_DARK_BG} alt="EBG Realty" className="h-11 w-auto invert opacity-90"/>
                        </Link>

                        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]">Sign In</div>
                        <h2 className="font-serif text-3xl md:text-4xl mt-2 text-black">Welcome back</h2>
                        <p className="text-black/60 mt-1 text-[13px]">Sign in to access your dashboard</p>

                        {/* Role tiles — compact */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5" data-testid="role-tabs">
                            {ROLES.map((r) => {
                                const active = role === r.k;
                                return (
                                    <button
                                        key={r.k}
                                        type="button"
                                        onClick={() => setRole(r.k)}
                                        data-testid={`role-${r.k}`}
                                        className={`relative p-3 border transition-all text-center ${active ? "border-black bg-black text-white" : "border-black/12 bg-white hover:border-black"}`}
                                    >
                                        <r.icon size={17} className="mx-auto"/>
                                        <div className="text-[10px] uppercase tracking-[0.16em] font-bold mt-1.5 leading-tight">{r.l}</div>
                                        <div className={`w-5 h-px my-1 mx-auto ${active ? "bg-[var(--ebg-gold)]" : "bg-[var(--ebg-gold)]/40"}`}/>
                                        <div className={`text-[9px] leading-snug ${active ? "text-white/70" : "text-black/55"}`}>{r.desc}</div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Form */}
                        <form onSubmit={submit} className="mt-5 space-y-3.5">
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-1.5">Email or Mobile Number</div>
                                <div className="relative">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                    <input required data-testid="login-email" type="text" placeholder="Enter your email or mobile number" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-black/15 bg-white pl-10 pr-3 py-2.5 text-[13px] outline-none focus:border-[var(--ebg-gold)] focus:ring-1 focus:ring-[var(--ebg-gold)]/20"/>
                                </div>
                            </label>

                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-1.5">Password</div>
                                <div className="relative">
                                    <Lock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40"/>
                                    <input required data-testid="login-password" type={showPw ? "text" : "password"} placeholder="Enter your password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full border border-black/15 bg-white pl-10 pr-10 py-2.5 text-[13px] outline-none focus:border-[var(--ebg-gold)] focus:ring-1 focus:ring-[var(--ebg-gold)]/20"/>
                                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black" data-testid="login-toggle-pw">
                                        {showPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                                    </button>
                                </div>
                            </label>

                            <div className="flex items-center justify-between text-[12px]">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <span className={`w-4 h-4 border flex items-center justify-center transition-colors ${remember ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)]" : "border-black/25"}`}>
                                        {remember && <Check size={11} className="text-black"/>}
                                    </span>
                                    <input type="checkbox" className="hidden" checked={remember} onChange={(e) => setRemember(e.target.checked)}/>
                                    <span className="text-black/70">Remember me</span>
                                </label>
                                <button type="button" className="text-[var(--ebg-gold)] font-bold hover:text-black" onClick={() => toast.info("Password reset link is sent to your registered email (demo).")}>
                                    Forgot password?
                                </button>
                            </div>

                            <button disabled={busy} type="submit" data-testid="login-submit" className="w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.28em] font-bold disabled:opacity-60 hover:bg-black hover:text-[var(--ebg-gold)] transition-colors inline-flex items-center justify-center gap-2">
                                {busy ? "Signing in…" : (<>Sign In <ArrowRight size={13}/></>)}
                            </button>
                        </form>

                        {/* Social */}
                        <div className="mt-5">
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-black/12"/>
                                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-black/45">Or continue with</div>
                                <div className="flex-1 h-px bg-black/12"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <button type="button" onClick={() => toast.info("Google sign-in coming soon")} data-testid="login-google" className="border border-black/15 bg-white py-2.5 text-[12px] font-bold text-black/80 hover:border-black inline-flex items-center justify-center gap-2">
                                    <GoogleIcon/> Google
                                </button>
                                <button type="button" onClick={() => toast.info("Microsoft sign-in coming soon")} data-testid="login-microsoft" className="border border-black/15 bg-white py-2.5 text-[12px] font-bold text-black/80 hover:border-black inline-flex items-center justify-center gap-2">
                                    <MicrosoftIcon/> Microsoft
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 text-center text-[12px] text-black/60">
                            New to EBG Realty?{" "}
                            <Link to={`/register?role=${role}`} data-testid="login-register-link" className="text-[var(--ebg-gold)] font-bold hover:text-black inline-flex items-center gap-1">
                                Create an account <ArrowRight size={11}/>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

/* --- Brand icons --- */
export function GoogleIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.7 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 34.9 26.8 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.5 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.2-.1-2.4-.4-3.5z"/>
        </svg>
    );
}
export function MicrosoftIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24">
            <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
            <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
            <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
            <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
        </svg>
    );
}
